import Splide, { Options } from '@splidejs/splide';

export function pairedSliders(): void {
    const sliderPairs = document.querySelectorAll<HTMLElement>('.splide-pair');

    if (sliderPairs.length === 0) return;

    sliderPairs.forEach((pair, pairIndex) => {
        const textEl = pair.querySelector<HTMLElement>('.splide-text');
        const imagesEl = pair.querySelector<HTMLElement>('.splide-images');

        if (!textEl || !imagesEl) return;

        // Generate unique IDs for this slider instance
        const uniqueId = `slider-${pairIndex}`;
        const textTrackId = `text-track-${uniqueId}`;
        const imagesTrackId = `images-track-${uniqueId}`;

        // Add IDs to the tracks
        const textTrack = textEl.querySelector('.splide__track');
        const imagesTrack = imagesEl.querySelector('.splide__track');

        if (textTrack) textTrack.id = textTrackId;
        if (imagesTrack) imagesTrack.id = imagesTrackId;

        // Create Splide instances
        const textOptions: Options = {
            type: 'fade',
            rewind: true,
            pagination: false,
            arrows: true,
            arrowPath: 'M 25.912216,34.175412 24.328359,32.647051 35.77786,21.197549 H 0 V 18.977707 H 35.820481 L 24.383855,7.5283613 25.912216,6 40,20.087628 Z',
        };

        const imagesOptions: Options = {
            gap: 24,
            pagination: false,
            type: 'loop',
            perMove: 1,
            focus: 'center',
            drag: true,
            arrows: false,
            breakpoints: {
                1024: {
                    gap: 16,
                    fixedWidth: '90%'
                },
                1440: {
                    gap: 24,
                    fixedWidth: '85%'
                }
            }
        };

        const text = new Splide(textEl, textOptions);
        const images = new Splide(imagesEl, imagesOptions);

        // Custom arrow functionality with dynamic accessibility
        const setupCustomArrows = () => {
            const parentContainer = pair.parentElement;

            if (!parentContainer) return;

            const prevArrow = parentContainer.querySelector<HTMLElement>('.splide__arrow--prev');
            const nextArrow = parentContainer.querySelector<HTMLElement>('.splide__arrow--next');

            // Show arrows by default (unless there's only one slide)
            const totalSlides = images.length;
            const shouldShowArrows = totalSlides > 1;

            const customArrows = parentContainer.querySelectorAll<HTMLElement>('.splide__arrow--prev, .splide__arrow--next');
            customArrows.forEach(arrow => {
                arrow.style.display = shouldShowArrows ? 'flex' : 'none';
            });

            // Function to update accessibility attributes
            const updateAccessibilityAttributes = () => {
                const currentIndex = images.index + 1;

                // Get slider heading for context (if available)
                const headingEl = parentContainer.querySelector('.eyebrow');
                const sliderName = headingEl?.textContent?.trim() || 'Image slider';

                if (prevArrow) {
                    const prevText = `Previous slide in ${sliderName} (currently ${currentIndex} of ${totalSlides})`;

                    prevArrow.setAttribute('aria-label', prevText);
                    prevArrow.setAttribute('title', prevText);
                    prevArrow.setAttribute('aria-controls', imagesTrackId);
                }

                if (nextArrow) {
                    const nextText = `Next slide in ${sliderName} (currently ${currentIndex} of ${totalSlides})`;

                    nextArrow.setAttribute('aria-label', nextText);
                    nextArrow.setAttribute('title', nextText);
                    nextArrow.setAttribute('aria-controls', imagesTrackId);
                }
            };

            // Set initial accessibility attributes immediately (before mounting)
            if (totalSlides > 0) {
                const headingEl = parentContainer.querySelector('.eyebrow');
                const sliderName = headingEl?.textContent?.trim() || 'Image slider';

                if (prevArrow) {
                    const prevText = `Previous slide in ${sliderName} (currently 1 of ${totalSlides})`;
                    prevArrow.setAttribute('aria-label', prevText);
                    prevArrow.setAttribute('title', prevText);
                    prevArrow.setAttribute('aria-controls', imagesTrackId);
                }

                if (nextArrow) {
                    const nextText = `Next slide in ${sliderName} (currently 1 of ${totalSlides})`;
                    nextArrow.setAttribute('aria-label', nextText);
                    nextArrow.setAttribute('title', nextText);
                    nextArrow.setAttribute('aria-controls', imagesTrackId);
                }
            }

            // Set up click handlers
            if (prevArrow) {
                prevArrow.addEventListener('click', (e) => {
                    e.preventDefault();
                    images.go('<');
                });
            }

            if (nextArrow) {
                nextArrow.addEventListener('click', (e) => {
                    e.preventDefault();
                    images.go('>');
                });
            }

            // Update accessibility attributes on mount and move
            images.on('mounted move', () => {
                updateAccessibilityAttributes();

                // Since type is 'loop', arrows should never be disabled
                if (prevArrow) prevArrow.classList.remove('disabled');
                if (nextArrow) nextArrow.classList.remove('disabled');
            });
        };

        // Handle overflow for images slider - SIMPLIFIED
        images.on('overflow', (isOverflow: boolean) => {
            images.options = {
                arrows: false,
                pagination: isOverflow,
                drag: isOverflow,
            };
            // Note: We no longer hide/show arrows here since they should always be visible
        });

        // Handle progress bar (sibling element of the pair)
        const bar = pair.nextElementSibling?.querySelector<HTMLElement>('.pair-progress-bar');
        text.on('mounted move', () => {
            const end = text.Components.Controller.getEnd() + 1;
            const rate = Math.min((text.index + 1) / end, 1);
            if (bar) {
                bar.style.width = `${100 * rate}%`;
            }
        });

        // Animate active slide image from 85% to 100%
        const updateActiveImageSizes = () => {
            images.Components.Slides.forEach((Slide) => {
                const img = Slide.slide.querySelector<HTMLImageElement>('img');
                if (!img) return;

                img.style.transition = 'width 0.3s ease';
                img.style.width = Slide.index === images.index ? '100%' : '85%';
            });
        };

        images.on('mounted move', updateActiveImageSizes);

        // Ensure sizing runs after images finish loading (race with lazy loading).
        images.on('mounted', () => {
            const slideImages: HTMLImageElement[] = [];

            images.Components.Slides.forEach((Slide) => {
                const img = Slide.slide.querySelector<HTMLImageElement>('img');
                if (img) slideImages.push(img);
            });

            if (slideImages.length === 0) return;

            let remaining = slideImages.length;
            const handleImageReady = () => {
                remaining -= 1;
                if (remaining <= 0) {
                    images.refresh();
                    updateActiveImageSizes();
                }
            };

            slideImages.forEach((img) => {
                if (img.complete) {
                    handleImageReady();
                } else {
                    img.addEventListener('load', handleImageReady, { once: true });
                    img.addEventListener('error', handleImageReady, { once: true });
                }
            });
        });

        // Run another refresh on full page load as a safety net for late assets.
        window.addEventListener('load', () => {
            images.refresh();
            updateActiveImageSizes();
        }, { once: true });

        // Sync the sliders
        text.sync(images);

        // Mount them
        images.mount();
        text.mount();

        // Setup custom arrows after mounting
        setupCustomArrows();
    });
}